import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Tooltip,
  Avatar,
  Button,
  CircularProgress,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FORMATS, PROCESS_ACTIVITY_STATUSES } from '../../utility/constants'
import { Box } from '@mui/system'
import React, { useCallback, useMemo, useState } from 'react'
import ConfirmationBox from './ConfirmationBox'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import DataPreview from '../DataPreview/DataPreview'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined'
import { fileServices } from '../../services/fileServices'
import { useAuthContext } from '../../hooks/useAuthContext'
import StandaloneFilePreview from './PreviewComponent/components/StandaloneFilePreview'
import TaskKebabMenu from './TaskKebabMenu'
import { useDeadlineCountdown } from '../../hooks/useDeadlineCountdown'
import { useMyTasks } from '../../context/MyTasksContext'
import MyTasksCardField from './MyTasksCardField'

function TaskCard({
  task,
  deleteTask,
  setIsDelegateOpen,
  setTask,
  setProcessReferenceNumber,
  teamDetails,
  visibleDataMap,
  onNavigate,
}) {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const { saveScrollPosition } = useMyTasks()
  const [filePreview, setFilePreview] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [openMenuIndex, setOpenMenuIndex] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const deadline = useDeadlineCountdown(
    task.process_activity_status === PROCESS_ACTIVITY_STATUSES.ACTIVE
      ? task.auto_close_deadline
      : null,
  )

  const confirmationText = `Are you sure you want to delete ${task.process_reference.toUpperCase()} task?`
  const { cardIcons, datetitle, cardtitle, cardSubtitle } = useResponsiveTypography()

  const [selectedPreviewProcessId, setSelectedPreviewProcessId] = useState(null)

  const downloadFile = useCallback(
    async (fileKey) => {
      try {
        await fileServices.downloadFile(user, fileKey)
      } catch (error) {
        console.error('Download failed:', error)
      }
    },
    [user],
  )

  const openDeleteDialog = useCallback(() => {
    setIsDialogOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteTask(task)
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTask, task])

  const openDelegateDialog = useCallback(
    (task) => {
      setTask(task)
      setProcessReferenceNumber(task.process_reference)
      setIsDelegateOpen(true)
    },
    [setTask, setProcessReferenceNumber, setIsDelegateOpen],
  )

  const extractedValues = visibleDataMap?.get(task.process_id) || []

  const efficiency = useMemo(() => {
    const startTime = moment(task.process_data_created_at)
    const endTime = moment(task.process_data_modified_at)
    const totalTimeTaken = moment.duration(endTime.diff(startTime))
    const totalMinutes = Math.max(
      1,
      totalTimeTaken.days() * 1440 + totalTimeTaken.hours() * 60 + totalTimeTaken.minutes(),
    )
    return Math.round((task.expected_minutes_to_complete * 100) / totalMinutes)
  }, [
    task.process_data_created_at,
    task.process_data_modified_at,
    task.expected_minutes_to_complete,
  ])

  const actionLabel = useMemo(
    () =>
      ({
        'data-entry': 'Fill Data',
        review: 'Review',
        'long-running': task.process_data_modified_by === user.id ? 'Fill Data' : 'Review',
      })[task.activity_type] || 'Work On Task',
    [task.activity_type, task.process_data_modified_by, user.id],
  )

  const handleNavigate = useCallback(() => {
    const navCallback = () =>
      navigate(`/process-data/${task.process_id}/${task.activity_template_id}`)

    saveScrollPosition()

    if (typeof onNavigate === 'function') {
      onNavigate(navCallback)
    } else {
      navCallback()
    }
  }, [navigate, saveScrollPosition, onNavigate, task.process_id, task.activity_template_id])

  return (
    <>
      <section className="task-card">
        <Card
          className="blog-box"
          elevation={1}
          sx={{
            ':hover': { boxShadow: 0 },
            boxShadow: 0,
            position: 'relative',
          }}
        >
          {isDeleting && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: 'inherit',
              }}
            >
              <CircularProgress size={28} color="primary" />
            </Box>
          )}

          <div className="task-groupicon">
            <Box sx={{ ...cardIcons, display: 'flex', justifyContent: 'flex-end' }}>
              <TaskKebabMenu
                task={task}
                openDeleteDialog={openDeleteDialog}
                openDelegateDialog={openDelegateDialog}
                onPreviewClick={setSelectedPreviewProcessId}
                teamDetails={teamDetails}
              />
            </Box>
          </div>

          <div className="task-card-header">
            <CardHeader
              sx={{ padding: '0px 0px' }}
              avatar={
                <Avatar
                  sx={{
                    bgcolor: task.color_code,
                    width: 30,
                    height: 30,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {task.process_reference[0].toUpperCase()}
                </Avatar>
              }
              title={
                <Typography
                  className="refrence-number"
                  style={{ fontSize: cardtitle, color: '#27104E', fontWeight: '700' }}
                >
                  {task.process_reference}
                </Typography>
              }
              subheader={
                <Typography
                  className="taskprocess-name text-dotted"
                  style={{ fontSize: cardSubtitle, color: '#4B4F60', fontWeight: '500' }}
                >
                  {task.process_template_name}
                </Typography>
              }
            />

            {isDialogOpen && (
              <ConfirmationBox
                title={confirmationText}
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                onYes={handleConfirmDelete}
              />
            )}
          </div>

          <div className="task-card-content">
            <CardContent sx={{ padding: '0px 0px 0px 0px !important' }}>
              <Typography
                className="mb-1"
                variant="body2"
                sx={{ fontSize: cardtitle, lineHeight: '10px' }}
                color="textSecondary"
              >
                <span style={{ color: '#27104E', fontWeight: '600' }}>
                  {`${task.activity_template_name} (version ${task.process_version}) `}
                </span>
              </Typography>

              {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.CLOSED && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ElectricBoltOutlinedIcon color="info" sx={{ fontSize: '14px' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: datetitle }}
                    color="textSecondary"
                    className="textSecondary fontWeight500"
                  >
                    {efficiency > 100 ? <>Greater than 100%</> : <>{efficiency}%</>}
                  </Typography>
                </Box>
              )}

              {extractedValues.map((item, index) => (
                <MyTasksCardField
                  key={index}
                  item={item}
                  index={index}
                  openMenuIndex={openMenuIndex}
                  setOpenMenuIndex={setOpenMenuIndex}
                  setFilePreview={setFilePreview}
                  downloadFile={downloadFile}
                />
              ))}

              {/* Deadline Countdown */}
              {deadline &&
                (() => {
                  const { color, fontWeight } = deadline.isExpired
                    ? { color: '#C62828', fontWeight: 700 }
                    : deadline.isWarning
                      ? { color: '#ED6C02', fontWeight: 700 }
                      : { color: '#4B4F60', fontWeight: 600 }

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        mt: '2px',
                        mb: '-4px',
                      }}
                    >
                      <AccessTimeIcon color="warning" style={{ fontSize: '14px' }} />
                      <Typography variant="body2" sx={{ fontSize: datetitle, color, fontWeight }}>
                        {deadline.text}
                      </Typography>
                    </Box>
                  )
                })()}

              <Box
                className="mb-0"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'end',
                  margin: '0px 0px 0px 0px',
                }}
              >
                {/* LEFT: Calendar Details */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CalendarMonthOutlinedIcon color="info" sx={{ fontSize: '14px' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: datetitle }}
                    color="textSecondary"
                    className="textSecondary fontWeight600"
                  >
                    {moment(new Date(task.process_data_created_at)).format(FORMATS.DATETIME)}
                  </Typography>

                  {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.CLOSED && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          height: '12px',
                          borderLeft: '1px solid #656D92',
                          marginLeft: '6px',
                          marginRight: '6px',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: datetitle }}
                        color="textSecondary"
                        className="textSecondary fontWeight600"
                      >
                        {moment(new Date(task.process_data_modified_at)).format(FORMATS.DATETIME)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.ACTIVE && (
                  <Tooltip title={actionLabel}>
                    <Button
                      variant="contained"
                      sx={{
                        border: '1px solid #6C47E4',
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                        fontWeight: '700 !important',
                        fontSize: {
                          lg: '9px !important',
                          sm: '8px !important',
                          xs: '8px !important',
                        },
                        padding: { xs: '4px 6px', lg: '4px 6px' },
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          border: '1px solid #6C47E4',
                          boxShadow: 'none',
                          color: '#fff',
                        },
                      }}
                      onClick={handleNavigate}
                    >
                      {actionLabel}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </div>
        </Card>
      </section>

      {selectedPreviewProcessId ? (
        <DataPreview
          onClose={() => setSelectedPreviewProcessId(null)}
          processId={selectedPreviewProcessId}
          open={!!selectedPreviewProcessId}
        />
      ) : null}

      <StandaloneFilePreview
        fileKey={filePreview}
        open={!!filePreview}
        onClose={() => setFilePreview(null)}
      />
    </>
  )
}

export default React.memo(TaskCard)
